<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import LaisBreadcrumb from './LaisBreadcrumb.vue';

const props = defineProps({
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  helper: { type: String, default: '' },
  breadcrumb: { type: Array, default: () => [] },
  showLogo: { type: Boolean, default: false },
  titleBadge: { type: String, default: '' },
  showBack: { type: Boolean, default: false },
  backLabel: { type: String, default: 'Voltar' },
  sticky: { type: Boolean, default: false },
});

const emit = defineEmits(['back']);
const store = useStore();
const globalConfig = computed(() => store.getters['globalConfig/get'] || {});
const logoSrc = computed(
  () => globalConfig.value.logo || '/brand-assets/logo.svg'
);
</script>

<template>
  <header
    class="flex min-w-0 shrink-0 flex-col items-start gap-4 bg-white px-4 pb-4 pt-[calc(1.25rem+env(safe-area-inset-top))] sm:px-6 sm:pt-5 md:flex-row md:items-start md:justify-between md:gap-6 lg:px-10 lg:pt-6"
    :class="
      sticky
        ? 'sticky top-0 z-20 border-b border-lais-border-soft max-md:static'
        : ''
    "
  >
    <div class="flex min-w-0 flex-1 flex-col gap-2">
      <div v-if="props.showLogo" class="flex items-center">
        <img
          :src="logoSrc"
          :alt="globalConfig.installationName || 'Lais'"
          class="h-8 w-auto shrink-0"
        />
      </div>
      <LaisBreadcrumb v-if="breadcrumb.length" :items="breadcrumb" />
      <div class="flex min-w-0 items-start gap-3">
        <button
          v-if="showBack"
          type="button"
          class="mt-0.5 grid size-9 shrink-0 place-items-center rounded-full border border-lais-border text-lais-text-muted transition hover:bg-lais-primary-50 hover:text-lais-primary"
          :aria-label="backLabel"
          @click="emit('back')"
        >
          <span class="i-lucide-arrow-left size-4" aria-hidden="true" />
        </button>
        <h1
          v-if="title"
          class="flex min-w-0 flex-wrap items-center gap-2 font-redHatDisplay text-[24px] font-semibold leading-tight text-lais-text sm:text-[28px]"
        >
          <span>{{ title }}</span>
          <span
            v-if="titleBadge"
            class="rounded-full bg-lais-mint/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-[#178565]"
          >
            {{ titleBadge }}
          </span>
        </h1>
      </div>
      <p
        v-if="helper"
        class="max-w-[680px] text-[14px] leading-snug text-lais-text-body"
      >
        {{ helper }}
      </p>
      <p
        v-else-if="subtitle"
        class="max-w-[680px] text-[14px] leading-snug text-lais-text-body"
      >
        {{ subtitle }}
      </p>
    </div>
    <div
      class="flex w-full min-w-0 flex-wrap items-center gap-2 md:w-auto md:max-w-[56%] md:justify-end"
    >
      <slot name="actions" />
    </div>
  </header>
</template>
